/**
 * Vision 인식 결과 확인/수정 컴포넌트
 *
 * Vision API가 인식한 답안 목록을 표시하고
 * 학생이 수정할 수 있는 UI 제공
 *
 * - 인식된 답안 목록 (스크롤)
 * - 각 항목: 문제번호, 인식답안(수정가능), O/X/?아이콘, 신뢰도 표시
 * - 인식 실패 항목은 빨간색 강조 + 수동 입력 유도
 * - 하단: "다시 찍기" / "채점 확정" 버튼
 */

import React, { useState } from 'react';
import { PixelButton } from '../common/PixelButton';
import { LatexText } from '../common/LatexText';
import { EditableAnswer } from '../../types/vision';

/**
 * VisionResultReview Props
 */
interface VisionResultReviewProps {
  /** 편집 가능한 답안 목록 */
  answers: EditableAnswer[];

  /** 전체 신뢰도 (0.0 ~ 1.0) */
  overallConfidence: number;

  /** 답안 수정 콜백 */
  onUpdateAnswer: (problemNumber: string, newAnswer: string) => void;

  /** 채점 확정 콜백 */
  onConfirm: () => void;

  /** 다시 찍기 콜백 */
  onRetake: () => void;

  /** 확정 중 로딩 상태 */
  isConfirming?: boolean;

  /** 채팅으로 풀기로 보류된 문제번호 목록 */
  deferredProblems?: string[];

  /** 문제를 채팅 채점으로 보류 콜백 */
  onDeferProblem?: (problemNumber: string) => void;

  /** 보류된 문제 취소 콜백 */
  onUndeferProblem?: (problemNumber: string) => void;
}

/**
 * 신뢰도에 따른 색상 반환
 */
const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 0.8) return 'text-green-400';
  if (confidence >= 0.6) return 'text-yellow-400';
  return 'text-red-400';
};

/**
 * 신뢰도 바 색상 클래스
 */
const getConfidenceBarColor = (confidence: number): string => {
  if (confidence >= 0.8) return 'bg-green-500';
  if (confidence >= 0.6) return 'bg-yellow-500';
  return 'bg-red-500';
};

/**
 * 채점 결과 아이콘
 */
const getResultIcon = (isCorrect: boolean | null, isUnreadable: boolean): string => {
  if (isUnreadable) return '❓';
  if (isCorrect === null) return '📝'; // T타입 (수동채점)
  return isCorrect ? '⭕' : '❌';
};

/**
 * Vision 인식 결과 확인/수정 컴포넌트
 */
export const VisionResultReview: React.FC<VisionResultReviewProps> = ({
  answers,
  overallConfidence,
  onUpdateAnswer,
  onConfirm,
  onRetake,
  isConfirming = false,
  deferredProblems = [],
  onDeferProblem,
  onUndeferProblem,
}) => {
  // 현재 편집 중인 문제 번호
  const [editingProblem, setEditingProblem] = useState<string | null>(null);

  // 통계 계산 (보류된 문제 제외)
  const nonDeferredAnswers = answers.filter(a => !deferredProblems.includes(a.problem_number));
  const correctCount = nonDeferredAnswers.filter(a => a.is_correct === true).length;
  const wrongCount = nonDeferredAnswers.filter(a => a.is_correct === false).length;
  const unreadableCount = nonDeferredAnswers.filter(a => a.is_unreadable).length;
  const pendingCount = nonDeferredAnswers.filter(a => a.is_correct === null && !a.is_unreadable).length;
  const deferredCount = deferredProblems.length;

  /**
   * 답안 입력 변경 핸들러
   */
  const handleAnswerChange = (problemNumber: string, value: string) => {
    onUpdateAnswer(problemNumber, value);
  };

  /**
   * 인라인 편집 시작
   */
  const handleStartEdit = (problemNumber: string) => {
    setEditingProblem(problemNumber);
  };

  /**
   * 인라인 편집 완료
   */
  const handleEndEdit = () => {
    setEditingProblem(null);
  };

  return (
    <div className="space-y-4">
      {/* 헤더: 전체 신뢰도 + 통계 */}
      <div className="bg-gray-800 rounded-lg p-4 border-2 border-gray-700">
        {/* 신뢰도 바 */}
        <div className="mb-3">
          <div className="flex justify-between items-center mb-1">
            <span className="font-pixel text-xs text-gray-400">인식 신뢰도</span>
            <span className={`font-pixel text-xs ${getConfidenceColor(overallConfidence)}`}>
              {Math.round(overallConfidence * 100)}%
            </span>
          </div>
          <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full ${getConfidenceBarColor(overallConfidence)} transition-all duration-300`}
              style={{ width: `${overallConfidence * 100}%` }}
            />
          </div>
        </div>

        {/* 통계 */}
        <div className="grid grid-cols-5 gap-2 text-center">
          <div>
            <p className="font-pixel text-lg text-green-400">{correctCount}</p>
            <p className="font-pixel text-xs text-gray-500">정답</p>
          </div>
          <div>
            <p className="font-pixel text-lg text-red-400">{wrongCount}</p>
            <p className="font-pixel text-xs text-gray-500">오답</p>
          </div>
          <div>
            <p className="font-pixel text-lg text-yellow-400">{unreadableCount}</p>
            <p className="font-pixel text-xs text-gray-500">미인식</p>
          </div>
          <div>
            <p className="font-pixel text-lg text-blue-400">{pendingCount}</p>
            <p className="font-pixel text-xs text-gray-500">서술형</p>
          </div>
          <div>
            <p className="font-pixel text-lg text-purple-400">{deferredCount}</p>
            <p className="font-pixel text-xs text-gray-500">채팅</p>
          </div>
        </div>
      </div>

      {/* 안내 메시지 */}
      <p className="font-pixel text-xs text-center text-gray-400">
        📝 답안을 확인하고 틀린 부분은 수정해주세요
      </p>

      {/* 답안 목록 */}
      <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
        {answers.map((answer) => {
          const isDeferred = deferredProblems.includes(answer.problem_number);
          // 보류 가능 조건: 인식 실패 또는 오답 (정답이 아닌 경우)
          const canDefer = (answer.is_unreadable || answer.is_correct === false) && onDeferProblem;

          return (
            <div
              key={answer.problem_number}
              className={`
                rounded-lg p-3 border-2 transition-all
                ${isDeferred
                  ? 'bg-purple-900/30 border-purple-500/50'
                  : answer.is_unreadable
                    ? 'bg-red-900/30 border-red-500/50'
                    : answer.is_edited
                      ? 'bg-yellow-900/30 border-yellow-500/50'
                      : 'bg-gray-800 border-gray-700'
                }
              `}
            >
              {/* 보류된 문제: 간소화된 표시 */}
              {isDeferred ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-pixel text-sm text-[#9bbc0f]">
                      {answer.problem_number}번
                    </span>
                    <span className="font-pixel text-sm text-purple-400">
                      📝 채팅으로 풀기 예정
                    </span>
                  </div>
                  {onUndeferProblem && (
                    <button
                      onClick={() => onUndeferProblem(answer.problem_number)}
                      className="font-pixel text-xs text-gray-400 hover:text-white px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 transition-colors"
                    >
                      취소
                    </button>
                  )}
                </div>
              ) : (
                /* 일반 문제: 기존 표시 */
                <>
                  <div className="flex items-center gap-3">
                    {/* 문제 번호 */}
                    <div className="w-12 flex-shrink-0">
                      <span className="font-pixel text-sm text-[#9bbc0f]">
                        {answer.problem_number}번
                      </span>
                    </div>

                    {/* 답안 입력 */}
                    <div className="flex-1">
                      {editingProblem === answer.problem_number ? (
                        <input
                          type="text"
                          value={answer.edited_answer}
                          onChange={(e) => handleAnswerChange(answer.problem_number, e.target.value)}
                          onBlur={handleEndEdit}
                          onKeyDown={(e) => e.key === 'Enter' && handleEndEdit()}
                          autoFocus
                          className={`
                            w-full px-2 py-1 rounded
                            bg-black border-2 border-[#9bbc0f]
                            font-pixel text-sm text-white
                            focus:outline-none focus:ring-2 focus:ring-[#9bbc0f]
                          `}
                        />
                      ) : (
                        <button
                          onClick={() => handleStartEdit(answer.problem_number)}
                          className={`
                            w-full px-2 py-1 rounded text-left
                            bg-gray-900 hover:bg-gray-700
                            font-pixel text-sm
                            ${answer.is_unreadable
                              ? 'text-red-400 italic'
                              : 'text-white'
                            }
                            transition-colors
                          `}
                        >
                          {answer.is_unreadable && !answer.is_edited
                            ? '(인식 실패 - 터치하여 입력)'
                            : answer.edited_answer
                              ? <LatexText text={answer.edited_answer} />
                              : '(빈 답안)'}
                        </button>
                      )}
                    </div>

                    {/* 결과 아이콘 */}
                    <div className="w-8 flex-shrink-0 text-center">
                      <span className="text-xl">
                        {getResultIcon(answer.is_correct, answer.is_unreadable)}
                      </span>
                    </div>

                    {/* 신뢰도 (작은 표시) */}
                    <div className="w-10 flex-shrink-0 text-right">
                      <span className={`font-pixel text-xs ${getConfidenceColor(answer.confidence)}`}>
                        {Math.round(answer.confidence * 100)}%
                      </span>
                    </div>

                    {/* 채팅으로 풀기 버튼 (인식 실패 또는 오답인 경우만) */}
                    {canDefer && (
                      <button
                        onClick={() => onDeferProblem!(answer.problem_number)}
                        className="flex-shrink-0 font-pixel text-xs text-purple-400 hover:text-purple-300 px-2 py-1 rounded bg-purple-900/50 hover:bg-purple-800/50 border border-purple-500/50 transition-colors"
                        title="이 문제는 채팅으로 풀기"
                      >
                        💬
                      </button>
                    )}
                  </div>

                  {/* 수정됨 표시 */}
                  {answer.is_edited && (
                    <p className="font-pixel text-xs text-yellow-400 mt-1 ml-12">
                      ✏️ 수정됨 (원본: {answer.recognized_answer ? <LatexText text={answer.recognized_answer} /> : '없음'})
                    </p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* 경고 메시지 (미인식 항목 있을 때) */}
      {unreadableCount > 0 && (
        <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-3">
          <p className="font-pixel text-xs text-yellow-400 text-center">
            ⚠️ 인식되지 않은 {unreadableCount}개 문제가 있어요. 직접 입력해주세요!
          </p>
        </div>
      )}

      {/* 하단 버튼 */}
      <div className="grid grid-cols-2 gap-3 pt-2">
        <PixelButton
          variant="secondary"
          size="medium"
          onClick={onRetake}
          disabled={isConfirming}
        >
          📷 다시 찍기
        </PixelButton>
        <PixelButton
          variant="primary"
          size="medium"
          onClick={onConfirm}
          disabled={isConfirming}
        >
          {isConfirming ? '⏳ 처리중...' : '✅ 채점 확정'}
        </PixelButton>
      </div>
    </div>
  );
};

VisionResultReview.displayName = 'VisionResultReview';
